import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  TrendingUp, Wallet, ShieldAlert, DollarSign, FileText,
  FileSpreadsheet, Database, FileDown, ChevronLeft, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { fmtMoney } from "@/lib/format";

const MONTHS_IT = ["Januar", "Februar", "März", "April", "Nie", "Juni",
                   "Juli", "August", "September", "Oktober", "November", "Dezember"];

function currentMonthISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function shiftMonth(iso, delta) {
  const [y, m] = iso.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(iso) {
  const [y, m] = iso.split("-").map(Number);
  return `${MONTHS_IT[m - 1]} ${y}`;
}

export default function FinancePage() {
  const { user } = useAuth();
  const [month, setMonth] = useState(currentMonthISO());
  const [summary, setSummary] = useState(null);
  const [lines, setLines] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [s, l] = await Promise.all([
        api.get(`/finance/summary?month=${month}`),
        api.get(`/finance/lines?month=${month}`),
      ]);
      setSummary(s.data);
      setLines(l.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const download = async (fmt) => {
    try {
      const res = await api.get(`/finance/export.${fmt}?month=${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `finance_${month}.${fmt === "datev" ? "csv" : fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Export ${fmt.toUpperCase()} pronto`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler export");
    }
  };

  const utile = useMemo(() => {
    if (!summary) return 0;
    return (summary.commissions_gross || 0) - (summary.commissions_paid || 0) - (summary.storno_fund || 0);
  }, [summary]);

  return (
    <div className="space-y-6" data-testid="finance-page">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-uppercase text-violet-600">/ Finanzen · solo numeri</div>
          <h1 className="font-display font-extrabold text-4xl lg:text-5xl tracking-tight mt-1">
            Finanz-Cockpit
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            Monatliche Aggregate · Zahlungen · Stornofonds · Buchhaltungsexport.
          </p>
        </div>
        {/* Month switcher */}
        <div className="flex items-center gap-2 prime-card px-2 py-1.5" data-testid="month-switcher">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setMonth(shiftMonth(month, -1))} data-testid="month-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="text-sm font-display font-bold tracking-tight min-w-[140px] text-center" data-testid="month-label">
            {monthLabel(month)}
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setMonth(shiftMonth(month, +1))} data-testid="month-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={TrendingUp}
          label="Produktion"
          value={summary ? `CHF ${fmtMoney(summary.production)}` : "—"}
          hint={summary ? `${summary.policies_count} aktivierte Policen` : ""}
          color="text-foreground"
          testId="kpi-production"
        />
        <KpiCard
          icon={Wallet}
          label="Provisionen"
          value={summary ? `CHF ${fmtMoney(summary.commissions_gross)}` : "—"}
          hint="Brutto erzeugt"
          color="text-prime"
          accent="border-l-4 border-prime"
          testId="kpi-commissions"
        />
        <KpiCard
          icon={ShieldAlert}
          label="Stornofonds"
          value={summary ? `CHF ${fmtMoney(summary.storno_fund)}` : "—"}
          hint="10 % zurückbehalten"
          color="text-amber-700"
          accent="border-l-4 border-amber-500"
          testId="kpi-storno"
        />
        <KpiCard
          icon={DollarSign}
          label="Zahlungen"
          value={summary ? `CHF ${fmtMoney(summary.payments)}` : "—"}
          hint="Netto Ausgang"
          color="text-green-700"
          accent="border-l-4 border-green-500"
          testId="kpi-payments"
        />
      </div>

      {/* Secondary KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={TrendingUp} label="Aus Fonds freigegeben" value={summary ? `CHF ${fmtMoney(summary.storno_released)}` : "—"}
          hint="≥ 3 Monate aktiv"
          color="text-emerald-700" testId="kpi-released" />
        <KpiCard
          icon={ShieldAlert} label="Verlorene Stornos"
          value={summary ? `CHF ${fmtMoney(summary.storno_lost)}` : "—"}
          hint="Stornierte Policen"
          color="text-red-700" testId="kpi-lost" />
        <KpiCard
          icon={Wallet} label="Potenzieller Gewinn"
          value={`CHF ${fmtMoney(utile)}`}
          hint="Brutto − bezahlt − fondo"
          color="text-foreground" testId="kpi-utile" />
        <KpiCard
          icon={FileText} label="Policen aktiv (gesamt)"
          value={summary ? String(summary.active_policies_count) : "—"}
          hint="Kumuliertes Portfolio"
          color="text-foreground" testId="kpi-active-count" />
      </div>

      {/* Export band */}
      <div className="prime-card p-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="label-uppercase text-muted-foreground">Export contabilità</div>
            <div className="font-display font-bold text-lg">Daten exportieren von {monthLabel(month)}</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" className="gap-2" onClick={() => download("pdf")} data-testid="export-pdf">
              <FileText className="w-3.5 h-3.5 text-red-700" /> PDF
            </Button>
            <Button variant="outline" size="sm" className="gap-2" onClick={() => download("xlsx")} data-testid="export-xlsx">
              <FileSpreadsheet className="w-3.5 h-3.5 text-green-700" /> Excel
            </Button>
            <Button variant="outline" size="sm" className="gap-2" onClick={() => download("csv")} data-testid="export-csv">
              <FileDown className="w-3.5 h-3.5 text-sky-700" /> CSV
            </Button>
            <Button variant="outline" size="sm" className="gap-2" onClick={() => download("datev")} data-testid="export-datev">
              <Database className="w-3.5 h-3.5 text-violet-700" /> DATEV
            </Button>
          </div>
        </div>
      </div>

      {/* Bewegungen table */}
      <div className="prime-card">
        <div className="px-6 py-4 border-b border-border flex items-center gap-2">
          <Wallet className="w-4 h-4 text-violet-600" />
          <h2 className="font-display font-bold text-xl">Bewegungen des Monats ({lines.length})</h2>
        </div>
        {busy && lines.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">Lädt…</div>
        ) : lines.length === 0 ? (
          <div className="p-12 text-center">
            <Wallet className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">Keine Bewegung in {monthLabel(month)}.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="movimenti-table">
              <thead className="bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
                <tr className="text-left">
                  <th className="px-4 py-2 font-medium">Data</th>
                  <th className="px-4 py-2 font-medium">Typ</th>
                  <th className="px-4 py-2 font-medium">Gesellschaft</th>
                  <th className="px-4 py-2 font-medium">Beneficiario</th>
                  <th className="px-4 py-2 font-medium">Rolle</th>
                  <th className="px-4 py-2 font-medium text-right">%</th>
                  <th className="px-4 py-2 font-medium text-right">Brutto</th>
                  <th className="px-4 py-2 font-medium text-right">Bezahlt</th>
                  <th className="px-4 py-2 font-medium text-right">Storno</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border font-mono text-xs">
                {lines.map((l, i) => (
                  <tr key={i} className="hover:bg-secondary/30">
                    <td className="px-4 py-2">{l.Data}</td>
                    <td className="px-4 py-2 capitalize">{l.Typ}</td>
                    <td className="px-4 py-2 truncate max-w-[160px]">{l.Gesellschaft}</td>
                    <td className="px-4 py-2">{l.Beneficiario}</td>
                    <td className="px-4 py-2 uppercase text-[10px] font-bold">{l.Rolle}</td>
                    <td className="px-4 py-2 text-right">{l["%"]}%</td>
                    <td className="px-4 py-2 text-right">{fmtMoney(l["Brutto CHF"])}</td>
                    <td className="px-4 py-2 text-right text-green-700">{fmtMoney(l["Bezahlt CHF"])}</td>
                    <td className="px-4 py-2 text-right text-amber-700">{fmtMoney(l["Storno CHF"])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, hint, color = "text-foreground", accent = "", testId }) {
  return (
    <div className={`prime-card p-5 ${accent}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <div className="label-uppercase text-muted-foreground">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />}
      </div>
      <div className={`font-display font-extrabold text-2xl sm:text-3xl tracking-tight ${color}`}>
        {value}
      </div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}
